export type LocationOption = {
  id: string;
  label: string;
  latitude: number;
  longitude: number;
  kind: "place" | "zip";
};

type Place = LocationOption & { state: string; keys: string[] };
type Index = {
  places: Place[];
  zips: Map<string, LocationOption>;
  states: [string, string][];
};
let cached: Promise<Index> | undefined;
const normalize = (value: string) =>
  value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\b([a-z])\.\s*([a-z])\./g, "$1$2")
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\bsaint\b/g, "st")
    .replace(/\bmount\b/g, "mt");
const simpleName = (value: string) =>
  value.replace(
    / (?:city and borough|consolidated government|metropolitan government|unified government|municipality|comunidad|zona urbana|borough|village|town|city|CDP)$/i,
    "",
  );
// Census calls the familiar Honolulu urban area "Urban Honolulu"; retain its
// official display name while accepting the usual query.
const aliases: Record<string, string[]> = { "1571550": ["Honolulu"] };

function decode(raw: unknown): Index {
  if (!raw || typeof raw !== "object") throw new Error("Invalid location data");
  const data = raw as Record<string, unknown>;
  if (
    data.v !== 1 ||
    data.scale !== 10000 ||
    !Array.isArray(data.states) ||
    !Array.isArray(data.places) ||
    !Array.isArray(data.zips)
  )
    throw new Error("Invalid location data");
  const states: [string, string][] = data.states.map((row) => {
    if (
      !Array.isArray(row) ||
      row.length !== 2 ||
      !/^[A-Z]{2}$/.test(row[0]) ||
      typeof row[1] !== "string"
    )
      throw new Error("Invalid location state");
    return [row[0], normalize(row[1])];
  });
  const codes = new Set(states.map(([code]) => code));
  const coordinate = (value: unknown, max: number) => {
    if (
      typeof value !== "number" ||
      !Number.isFinite(value) ||
      Math.abs(value) > max * 10000
    )
      throw new Error("Invalid location point");
    return value / 10000;
  };
  const names = new Map<string, number>();
  for (const row of data.places) {
    if (
      !Array.isArray(row) ||
      row.length !== 5 ||
      !/^\d{7}$/.test(row[0]) ||
      typeof row[1] !== "string" ||
      !codes.has(row[2])
    )
      throw new Error("Invalid location place");
    const key = simpleName(row[1]) + ", " + row[2];
    names.set(key, (names.get(key) ?? 0) + 1);
  }
  const places = data.places.map((row): Place => {
    const [id, name, state, lat, lng] = row as [
      string,
      string,
      string,
      number,
      number,
    ];
    const base = simpleName(name),
      label = base + ", " + state;
    return {
      id: "place:" + id,
      label: (names.get(label) ?? 0) > 1 ? name + ", " + state : label,
      state,
      keys: [...new Set([base, name, ...(aliases[id] ?? [])].map(normalize))],
      kind: "place",
      latitude: coordinate(lat, 90),
      longitude: coordinate(lng, 180),
    };
  });
  const zips = new Map<string, LocationOption>();
  for (const row of data.zips) {
    if (!Array.isArray(row) || row.length !== 3 || !/^\d{5}$/.test(row[0]))
      throw new Error("Invalid location ZIP");
    zips.set(row[0], {
      id: "zip:" + row[0],
      label: "ZIP " + row[0],
      kind: "zip",
      latitude: coordinate(row[1], 90),
      longitude: coordinate(row[2], 180),
    });
  }
  return { places, zips, states };
}

async function load(): Promise<Index> {
  if (cached) return cached;
  const pending = (async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      // Only the static dataset is requested. Search text stays in this browser.
      const response = await fetch(
        `${import.meta.env.BASE_URL}data/us-locations.json`,
        { credentials: "same-origin", signal: controller.signal },
      );
      if (!response.ok) throw new Error("Location data unavailable");
      return decode(await response.json());
    } catch {
      throw new Error(
        "City and ZIP search could not load. Reconnect and try again; your existing results are still available.",
      );
    } finally {
      clearTimeout(timeout);
    }
  })();
  cached = pending;
  try {
    return await pending;
  } catch (error) {
    if (cached === pending) cached = undefined;
    throw error;
  }
}

/** Local Census area search. ZIPs are ZCTA representative points, not addresses. */
export async function searchPlaces(query: string): Promise<LocationOption[]> {
  const text = normalize(query.slice(0, 160));
  if (text.length < 2) return [];
  const index = await load();
  const postal = query.trim().match(/^(\d{5})(?:-\d{4})?$/);
  if (postal) {
    const result = index.zips.get(postal[1]);
    return result ? [{ ...result }] : [];
  }
  if (/^[\d\s-]+$/.test(query.trim())) return [];
  let city = text,
    state: string | undefined;
  const suffixes = index.states
    .flatMap(([code, name]) => [
      [code, normalize(code)],
      [code, name],
    ])
    .sort((a, b) => b[1].length - a[1].length);
  for (const [code, name] of suffixes) {
    if (text.endsWith(" " + name)) {
      city = text.slice(0, -name.length - 1).trim();
      state = code;
      break;
    }
  }
  if (city.length < 2) return [];
  return index.places
    .filter(
      (place) =>
        (!state || place.state === state) &&
        place.keys.some((key) => key.startsWith(city)),
    )
    .sort(
      (a, b) =>
        Number(b.keys.includes(city)) - Number(a.keys.includes(city)) ||
        a.label.localeCompare(b.label, "en") ||
        a.id.localeCompare(b.id),
    )
    .slice(0, 8)
    .map(({ state: _state, keys: _keys, ...place }) => place);
}
